import { ChevronDown, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";

import { getConfigWithToken } from "../config/config";
import { updateSuperAdminOrganizationById } from "../config/url";
import { US_STATES } from "../utils/usStates";
import classes from "./EditOrganizationDrawer.module.css";

const normalizeStateValue = (value) => {
	const trimmed = String(value || "").trim();
	if (!trimmed) return "";

	const byCode = US_STATES.find((option) => option.value.toUpperCase() === trimmed.toUpperCase());
	if (byCode) return byCode.value;

	const byLabel = US_STATES.find((option) => option.label.toLowerCase() === trimmed.toLowerCase());
	return byLabel?.value || trimmed;
};

const EditOrganizationDrawer = ({ isOpen, onClose, onSuccess, organization }) => {
	const token = useSelector((state) => state?.user?.user?.token) || localStorage.getItem("user_id");

	const [city, setCity] = useState("");
	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [name, setName] = useState("");
	const [serverError, setServerError] = useState("");
	const [state, setState] = useState("");

	const requestHeaders = useMemo(
		() => ({
			Accept: "application/json",
			"Content-Type": "application/json",
			...(token ? getConfigWithToken(token).headers : {}),
		}),
		[token],
	);

	useEffect(() => {
		if (!isOpen || !organization) return undefined;

		setName(organization.name || "");
		setCity(organization.city || "");
		setState(normalizeStateValue(organization.state));
		setErrors({});
		setServerError("");

		const onKeyDown = (event) => {
			if (event.key === "Escape") onClose();
		};

		document.addEventListener("keydown", onKeyDown);
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.body.style.overflow = "";
		};
	}, [isOpen, onClose, organization]);

	if (!isOpen || !organization) return null;

	const clearFieldError = (field) => {
		setErrors((previous) => {
			if (!previous[field]) return previous;
			const next = { ...previous };
			delete next[field];
			return next;
		});
		setServerError("");
	};

	const validate = () => {
		const nextErrors = {};

		if (!name.trim()) nextErrors.name = "Organization name is required.";
		if (!city.trim()) nextErrors.city = "City is required.";
		if (!state.trim()) nextErrors.state = "State is required.";

		setErrors(nextErrors);

		if (Object.keys(nextErrors).length) {
			toast.error("Please fix the highlighted fields before saving.");
		}

		return Object.keys(nextErrors).length === 0;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!validate()) return;

		setIsSubmitting(true);
		setServerError("");

		try {
			const response = await axios.patch(
				updateSuperAdminOrganizationById(organization.id),
				{ city: city.trim(), name: name.trim(), state: state.trim() },
				{ headers: requestHeaders },
			);

			if (response.data?.success === false) {
				const message = response.data?.message || "Failed to update organization.";
				setServerError(message);
				toast.error(message);
				return;
			}

			toast.success(response.data?.message || "Organization updated successfully.");
			onSuccess?.(response.data?.data);
			onClose();
		} catch (error) {
			const message = error.response?.data?.message || "Failed to update organization.";
			setServerError(message);
			toast.error(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<button
				aria-label="Close edit organization drawer"
				className={classes.backdrop}
				onClick={onClose}
				type="button"
			/>
			<aside aria-labelledby="edit-organization-title" aria-modal="true" className={classes.drawer} role="dialog">
				<div className={classes.header}>
					<div className={classes.headerTop}>
						<h2 className={classes.title} id="edit-organization-title">
							Edit Organization
						</h2>
						<button aria-label="Close" className={classes.closeButton} onClick={onClose} type="button">
							<X size={18} />
						</button>
					</div>
					<p className={classes.subtitle}>Update organization info</p>
				</div>

				<form className={classes.form} noValidate onSubmit={handleSubmit}>
					<div className={classes.body}>
						<section className={classes.formSection}>
							<p className={classes.sectionHeading}>Organization Info</p>

							<label className={classes.field}>
								<span className={classes.label}>Organization Name</span>
								<input
									aria-required="true"
									className={`${classes.input} ${errors.name ? classes.inputError : ""}`}
									onChange={(event) => {
										setName(event.target.value);
										clearFieldError("name");
									}}
									type="text"
									value={name}
								/>
								{errors.name ? <p className={classes.errorText}>{errors.name}</p> : null}
							</label>

							<div className={classes.gridTwo}>
								<label className={classes.field}>
									<span className={classes.label}>City</span>
									<input
										aria-required="true"
										className={`${classes.input} ${errors.city ? classes.inputError : ""}`}
										onChange={(event) => {
											setCity(event.target.value);
											clearFieldError("city");
										}}
										type="text"
										value={city}
									/>
									{errors.city ? <p className={classes.errorText}>{errors.city}</p> : null}
								</label>

								<div className={classes.field}>
									<span className={classes.label}>State</span>
									<div className={classes.selectWrap}>
										<select
											aria-required="true"
											className={`${classes.select} ${!state ? classes.selectPlaceholder : ""} ${
												errors.state ? classes.inputError : ""
											}`}
											onChange={(event) => {
												setState(event.target.value);
												clearFieldError("state");
											}}
											value={state}>
											<option value="">Select</option>
											{US_STATES.map((stateOption) => (
												<option key={stateOption.value} value={stateOption.value}>
													{stateOption.label}
												</option>
											))}
										</select>
										<ChevronDown aria-hidden="true" className={classes.selectIcon} size={14} strokeWidth={2} />
									</div>
									{errors.state ? <p className={classes.errorText}>{errors.state}</p> : null}
								</div>
							</div>
						</section>

						{serverError ? <div className={classes.serverError}>{serverError}</div> : null}
					</div>

					<div className={classes.footer}>
						<button className={classes.cancelButton} disabled={isSubmitting} onClick={onClose} type="button">
							Cancel
						</button>
						<button className={classes.submitButton} disabled={isSubmitting} type="submit">
							{isSubmitting ? "Saving..." : "Save Changes"}
						</button>
					</div>
				</form>
			</aside>
		</>
	);
};

export default EditOrganizationDrawer;
